import React, { useContext, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { kebabCase } from 'lodash';

import InfiniteScroll from 'react-infinite-scroller';

import classNames from 'classnames';

import {
  Button, Checkbox, Icon, Image, Loader, Pagination, Table
} from 'semantic-ui-react';

import iconInstructions from '../img/icon-instructions.svg';
import iconObservationalPencil from '../img/icon-observational-pencil.svg';

import iconPodium from '../img/icon-podium.svg';
import iconSurvey from '../img/icon-survey.svg';
import { ReactComponent as IconView } from '../img/icon-view.svg';

import '../css/GradebookDetailsTable.less';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { dateFormat, getContentTypeIcon, getStyleVar, makeKeyboardAccessible, stripImgHtmlTags } from '../utils';

import { ASSIGNMENT_STATUS, ASSIGNMENT_TYPE } from '../managers/AssignmentManager';

import {
  GRADEBOOK_RESPONSE_CONTENT_STATE, GRADEBOOK_RESPONSE_SUBMITTED_STATE, GRADEBOOK_SCORE_STATE,
  OBSERVATIONAL_GRADER_TOGGLE_STATUS
} from '../constants/GradebookConstants';

import GradebookDetailsService from '../services/gradebook/GradebookDetailsService';
import GradebookMainService from '../services/gradebook/GradebookMainService';
import PopupService from '../services/PopupService';
import UsageService from '../services/UsageService';
import UtilityService from '../services/UtilityService';
import iconAiScored from '../img/icon-ai-scored.svg';
import iconAiOverridden from '../img/icon-ai-overriden.svg'
import iconAiPending from '../img/icon-ai-pending.svg';

const GradebookDetailsTable = observer((props) => {
  const { t } = props;

  const { assignmentManager, gradebookManager } = useContext(MobXProviderContext);

  const {
    activeGradebookTable,
    activeGradebookType,
    allowLoadMoreAggregateTableRows,
    allowObservationalGrader,
    hasMoreAggregateTableRows,
    isGradebookDetails,
    isGradebookLikert,
    isGradebookTyping,
    isObservationalGraderOn
  } = gradebookManager;

  const isLoading = gradebookManager.gradebookManagerLoadingFlagActive;

  const isReadOnly = GradebookMainService.isGradebookReadOnly();

  const isLearnosity = props.assignmentEntityTypeId?.includes?.('learnosity');

  const PAGE_SIZE = gradebookManager.GRADEBOOK_DETAILS_TABLE_HORIZONTAL_SCORES_PAGE_SIZE;

  const column = gradebookManager.activeGradebookTableSortColumn;
  const direction = gradebookManager.activeGradebookTableSortDirection;

  const urlParams = new URLSearchParams(window.location.search);
  const assignmentId = urlParams.get('assignmentId');

  const [allowSort, setAllowSort] = useState(true);

  let [scrollerKey, setScrollerKey] = useState(0);

  // TODO remove; unused
  // const [loadingActivePopupContentFunction, setLoadingActivePopupContentFunction] = useState(false);

  const GradebookObservationalScoreCell = SatCoreComponent('GradebookObservationalScoreCell');
  const InfoIcon = SatCoreComponent('InfoIcon');

  // useEffect(() => {
  //   // placeholder
  // }, []);

  const handleToggleHideUnscorableColumns = async (_event, data) => {
    const shouldHideUnscorableColumns = data.checked;
    gradebookManager.setShouldHideUnscorableColumns(shouldHideUnscorableColumns);
    await props.refreshAllGradebookData();
  };

  const handleToggleObservationalGrader = async (_event, data) => {
    const shouldToggleOn = data.checked;
    let observationalGraderToggleStatus;
    if (shouldToggleOn) {
      observationalGraderToggleStatus = OBSERVATIONAL_GRADER_TOGGLE_STATUS.ACTIVE;
      gradebookManager.setObservationalGraderToggleStatus(observationalGraderToggleStatus);
      await props.refreshAllGradebookData();
    } else {
      observationalGraderToggleStatus = OBSERVATIONAL_GRADER_TOGGLE_STATUS.INACTIVE;
      gradebookManager.setObservationalGraderToggleStatus(observationalGraderToggleStatus);
      await props.refreshAllGradebookData();
    }
  };

  const handleLoadMore = async (page) => {
    const assignment = await assignmentManager.fetchActivity(assignmentId);

    const sortField = gradebookManager.activeGradebookTableSortColumn;
    const sortDirection = gradebookManager.activeGradebookTableSortDirection;

    const functions = {
      // TODO remove
      // isSpecialAggregateGradebookDetailsSortCase: GradebookDetailsService.isSpecialAggregateGradebookDetailsSortCase
    };
    await gradebookManager.fetchAggregateGradebookDetails({
      assignment,
      functions,
      page,
      sortDirection,
      sortField
    });
  };

  const handleSort = async (clickedColumnId, headerScoreIndex = -1) => {
    if (!allowSort || !clickedColumnId) {
      return;
    }

    // reset the infinite scroller to fix issue with page being misreported in handleLoadMore
    const newScrollerKey = ++scrollerKey;
    setScrollerKey(newScrollerKey);

    // TODO remove
    // const rowsTotalLength = gradebookManager.gradebookTableRowsTotalFromBackend || 0;

    if (allowLoadMoreAggregateTableRows) {
      gradebookManager.setAllowLoadMoreAggregateTableRows(false);

      // TODO remove
      // if (GradebookDetailsService.isSpecialAggregateGradebookDetailsSortCase(clickedColumnId, headerScoreIndex)) {
      //   if (rowsTotalLength <= 120) {
      //     await GradebookDetailsService.handleSpecialAggregateGradebookDetailsSortCase(
      //       clickedColumnId, headerScoreIndex
      //     );
      //   } else {
      //     gradebookManager.setAllowLoadMoreAggregateTableRows(true);
      //     return;
      //   }
      // } else {
      await GradebookDetailsService.handleGradebookDetailsSortOnBackend(clickedColumnId);
      // }
      gradebookManager.setAllowLoadMoreAggregateTableRows(true);
    }
  };

  const handleHorizontalScoresPageChange = async (_event, pageInfo) => {
    const totalPages = gradebookManager.gradebookDetailsTableHorizontalScoresPageTotal;
    if (totalPages > 1) {
      const { activePage } = pageInfo;
      gradebookManager.setActiveGradebookDetailsHorizontalScoresPage(activePage);
    }
  };

  const renderTableHeader = () => {
    const { hideDetailsDates, showDetailsGradeAsRawScore, showDetailsGradeAsPercentage } = gradebookManager;
    const activePage = gradebookManager.activeGradebookDetailsHorizontalScoresPage;

    const paginatedHeaderInfo = GradebookDetailsService.getPaginatedGradebookDetailsHeaderInfo();

    let gradeHeaderLabel = 'gradeHeaderLabel';
    if (isGradebookLikert) {
      gradeHeaderLabel = 'likertAverage';
    }

    return (
      <Table.Row
        className={isLoading ? 'top-header-row loading-row' : 'top-header-row'}
        disabled={isLoading}>

        {/* HEADER: ACTIVITY NUMBER (ASGMT) */}
        {renderGradebookTableHeaderCell({ _cellId: 'activityNumber' })}

        {/* HEADER: START DATE */}
        {!hideDetailsDates && (
          renderGradebookTableHeaderCell({
            _cellId: 'activityTimezoneStartDate',
            className: classNames('cell-start-date', activeGradebookType, activeGradebookTable)
          })
        )}

        {/* HEADER: END DATE */}
        {!hideDetailsDates && (
          renderGradebookTableHeaderCell({
            _cellId: 'activityTimezoneEndDate',
            className: classNames('cell-end-date', activeGradebookType, activeGradebookTable)
          })
        )}

        {/* HEADER: FIRST NAME */}
        {renderGradebookTableHeaderCell({ _cellId: 'firstName' })}

        {/* HEADER: LAST NAME */}
        {renderGradebookTableHeaderCell({ _cellId: 'lastName' })}

        {/* HEADER: USAGE */}
        {!isLearnosity && !isGradebookTyping && (
          renderGradebookTableHeaderCell({
            _cellId: 'viewedTime',
            className: classNames('cell-usage', activeGradebookType, activeGradebookTable),
            label: (<span>{t('viewedTimeHeader', 'Usage')}</span>)
          })
        )}

        {/* HEADER: GRADE or LIKERT GRADE ('Survey Average') */}
        {((showDetailsGradeAsPercentage && !isGradebookTyping) || isGradebookLikert) && !isObservationalGraderOn && (
          renderGradebookTableHeaderCell({
            _cellId: 'grade',
            label: (<span>{t(gradeHeaderLabel)}</span>)
          })
        )}

        {/* HEADER: GRADE AS RAW SCORE */}
        {(showDetailsGradeAsRawScore && !isGradebookLikert && !isGradebookTyping) && !isObservationalGraderOn && (
          renderGradebookTableHeaderCell({
            _cellId: 'grade',
            key: 'key-header-cell-score',
            label: (<span>{t('scoreHeaderLabel')}</span>)
          })
        )}

        {/* HEADER: SCORE INDEXES */}
        {paginatedHeaderInfo.map((cellHeaderInfo, headerScoreIndex) => {
          const { elementId, item, number, questionNumber, type: contentType } = cellHeaderInfo;

          const isInstruction = contentType === 'StudentInstruction' || contentType === 'TeacherInstruction';
          const isSurvey = !isInstruction && item === 'Survey';
          const isScorableSurvey = !isInstruction && item === 'ScorableSurvey';
          const isItem = item === 'Item';

          const trueHeaderScoreIndex = headerScoreIndex + ((activePage - 1) * PAGE_SIZE);

          const className = `cell-score ${trueHeaderScoreIndex} ${activeGradebookType}`;

          const slideNumberLabel = cellHeaderInfo.label;

          // Custom numbering: show the number. Auto + do-not-score: backend sends null so the survey icon shows.
          let headerItemNumberLabel;
          if (isSurvey || isScorableSurvey) {
            headerItemNumberLabel = `${number}` !== 'null'
              ? number
              : (<Image className='header-icon-survey' src={iconSurvey} />);
          } else if (isItem) {
            headerItemNumberLabel = `${number}` !== 'null' ? number : '—';
          } else if (isInstruction) {
            if (`${number}` !== 'null') {
              headerItemNumberLabel = number;
            } else {
              const icon = gradebookManager.shouldUsePodiumIconForInstructions ? iconPodium : iconInstructions;
              headerItemNumberLabel = (<Image className='header-icon-podium' src={icon} />);
            }
          } else {
            const contentTypeIcon = getContentTypeIcon(contentType);
            headerItemNumberLabel = (<Image className={classNames('header-icon', contentType)} src={contentTypeIcon} />);
          }

          let cellActivityElement, excludeFromScoring, isScorable, maxScore;

          // eslint-disable-next-line prefer-destructuring
          const students = gradebookManager.gradebookDetails.students;

          // For the table header exclusion checkbox, we don't want it to be based off the first students
          // exclusion. Instead we loop over the students and check the specific item. If any of the items
          // are not exluded then the header will show not exluded as well.
          if (students) {
            excludeFromScoring = true;
            for (let i = 0; i < students.length; i++) {
              if (students[i].activityElements[trueHeaderScoreIndex].excludeFromScoring === false) {
                excludeFromScoring = false;
              }
            }
          }
          // Use the first student to get the activity element for the current index.
          const student = students && students[0];
          if (student.activityElements) {
            cellActivityElement = student.activityElements[trueHeaderScoreIndex];
          }
          // eslint-disable-next-line prefer-destructuring
          const activityId = student.activityId;
          // TODO unused // const paginatedStudentMaxScores = GradebookDetailsService.getPaginatedGradebookDetailsStudentMaxScores(student);
          const questionStem = student.questionStems[trueHeaderScoreIndex];

          if (cellActivityElement) {
            if (cellActivityElement.scorable !== undefined) {
              isScorable = !!cellActivityElement.scorable;
            }
          }

          let clickedColumnId;
          if (cellHeaderInfo?.index || cellHeaderInfo?.index === 0) {
            clickedColumnId = `questionNum${cellHeaderInfo?.index}`;
          }

          const scoreLabelTrigger = headerItemNumberLabel;

          const scoreCellDetails = {
            activityId,
            cellActivityElement,
            cellHeaderInfo,
            elementId,
            excludeFromScoring,
            instanceInfo: student,
            isScorable,
            isSurvey,
            maxScore,
            // Header display uses `number` (null shows the icon); the tooltip still shows the sequential
            // question number via `questionNumber`, which the backend always sends.
            questionNum: (isItem || isSurvey || isScorableSurvey) ? (questionNumber ?? number) : null,
            questionStem,
            scoreIndex: trueHeaderScoreIndex,
            slideNumberLabel,
            tableSection: 'header'
          };

          let scoreLabelContent;

          if (isScorable || isSurvey || !isItem) {
            scoreLabelContent = {
              children: () => {
                if (typeof props.renderDetailsHeaderCellScoreLabelContent === 'function') {
                  return props.renderDetailsHeaderCellScoreLabelContent(scoreCellDetails);
                }
                return renderDetailsHeaderCellScoreLabelContent(scoreCellDetails);
              }
            };
          }
          return (
            <Table.HeaderCell
              key={`${trueHeaderScoreIndex}`}
              className={className}
              onClick={() => handleSort(clickedColumnId, trueHeaderScoreIndex)}
              sorted={clickedColumnId && (column === clickedColumnId) ? direction : null}>
              {(isScorable || isSurvey || !isItem) ? renderPopup(
                scoreLabelContent, scoreLabelTrigger,
                'cell-score-popup', 'top center', {
                  mouseEnterDelay: 400,
                  mouseLeaveDelay: 400,
                  offset: [0, 16]
                }) : scoreLabelTrigger}
            </Table.HeaderCell>
          );
        })}
      </Table.Row>
    );
  };

  const renderGradebookTableHeaderCell = ({
    _cellId = '',
    key,
    className,
    onClick,
    label,
    sorted
  } = {}) => {
    const dashedCellId = kebabCase(_cellId);

    key = key || `key-header-cell-${dashedCellId}`;
    className = className || classNames(`cell-${dashedCellId}`, activeGradebookType, activeGradebookTable);
    onClick = onClick || (() => handleSort(_cellId));
    sorted = sorted || (column === _cellId ? direction : null);
    label = label || (<span>{t(_cellId)}</span>);

    return (
      <Table.HeaderCell
        key={key}
        className={className}
        onClick={onClick}
        sorted={sorted}>
        {label}
      </Table.HeaderCell>
    );
  };

  const renderTableBodyRows = () => {
    const { headerInfo, students } = gradebookManager.gradebookDetails;
    const { getAssignmentNumberCell, t } = props;
    const { hideDetailsDates, showDetailsGradeAsRawScore, showDetailsGradeAsPercentage } = gradebookManager;

    const rows = [];

    const isDetailScoreCellReadOnly = isReadOnly || isLearnosity;

    // table body rows
    // eslint-disable-next-line array-callback-return
    students.map((student, studentIndex) => {
      // eslint-disable-next-line prefer-destructuring
      const activityId = student.activityId;
      const paginatedStudentMaxScores = GradebookDetailsService.getPaginatedGradebookDetailsStudentMaxScores(student);

      const paginatedStudentScores = GradebookDetailsService.getPaginatedGradebookDetailsStudentScores(student);
      const paginatedHeaderInfo = GradebookDetailsService.getPaginatedGradebookDetailsHeaderInfo();

      const emptyScoreCellsLength = paginatedHeaderInfo.length - paginatedStudentScores.length;
      for (let i = 0; i < emptyScoreCellsLength; i++) {
        paginatedStudentScores.push('—');
      }

      const activePage = gradebookManager.activeGradebookDetailsHorizontalScoresPage;

      const isSubmitted = !!(student && student.submitted);

      const commonProps = {
        instanceInfo: student,
        instanceInfoIndex: studentIndex,
        isReadOnly
      };

      rows.push(
        <Table.Row
          key={`key-data-row-${studentIndex}-${activityId}`}
          className={isLoading ? 'loading-row' : ''}
          disabled={isLoading}
          tabIndex={0}>

          {/* BODY: ACTIVITY NUMBER (ASGMT) */}
          {renderGradebookTableBodyCell({
            _cellId: 'activityNumber',
            label: (<span>{getAssignmentNumberCell(student, t)}</span>),
            tabIndex: -1,
            ...commonProps
          })}

          {/* BODY: START DATE */}
          {!hideDetailsDates && (
            renderGradebookTableBodyCell({
              _cellId: 'startDate',
              label: (<span>{dateFormat(student.activityTimezoneStartDate, 'twoDigitDate')}</span>),
              tabIndex: -1,
              ...commonProps
            })
          )}

          {/* BODY: END DATE */}
          {!hideDetailsDates && (
            renderGradebookTableBodyCell({
              _cellId: 'endDate',
              label: (<span>{dateFormat(student.activityTimezoneEndDate, 'twoDigitDate')}</span>),
              tabIndex: -1,
              ...commonProps
            })
          )}

          {/* BODY: FIRST NAME */}
          {renderGradebookTableBodyCell({
            _cellId: 'firstName',
            label: renderPopup(student.firstName),
            tabIndex: -1,
            ...commonProps
          })}

          {/* BODY: LAST NAME */}
          {renderGradebookTableBodyCell({
            _cellId: 'lastName',
            label: renderPopup(student.lastName),
            tabIndex: -1,
            ...commonProps
          })}

          {/* BODY: USAGE */}
          {!isLearnosity && !isGradebookTyping && (
            renderGradebookTableBodyCell({
              _cellId: 'usage',
              label: renderPopup(
                renderContentsWithLabel([
                  {
                    content: UsageService.getUnroundedUsageLabel(student.viewedTime),
                    translationKey: 'viewedTime'
                  }
                ]),
                UsageService.getUsageLabel({
                  lettersToAppendWhenNotZero: 'm',
                  lettersToAppendWhenZero: 's',
                  showUnroundedOnHover: false,
                  viewedTimeInSeconds: student.viewedTime
                }), 'cell-usage-popup'
              ),
              tabIndex: -1,
              ...commonProps
            })
          )}

          {/* BODY: GRADE or LIKERT GRADE ('Survey Average') */}
          {((showDetailsGradeAsPercentage && !isGradebookTyping) || isGradebookLikert) && !isObservationalGraderOn && (
            renderGradebookTableBodyCell({
              _cellId: 'grade',
              label: props.showGradeColumn(student, true),
              tabIndex: -1,
              ...commonProps
            })
          )}

          {/* BODY: RAW SCORE */}
          {(showDetailsGradeAsRawScore && !isGradebookLikert && !isGradebookTyping) && !isObservationalGraderOn && (
            renderGradebookTableBodyCell({
              _cellId: 'grade',
              key: 'key-data-cell-score-' + studentIndex + '-' + student?.activityId, // eslint-disable-line prefer-template
              label: props.showGradeColumn(student, false),
              tabIndex: -1,
              ...commonProps
            })
          )}

          {/* BODY: SCORE INDEXES */}
          {paginatedStudentScores.map((score, scoreIndex) => {
            if (score === '—') {
              return (
                <>
                  <Table.Cell
                    key={`key-data-cell-${activityId}-${(student.studentId || student.id)}-${scoreIndex}`}
                    className={classNames('cell-score empty', {
                      'is-read-only': isDetailScoreCellReadOnly
                    })}>
                    <span>—</span>
                  </Table.Cell>
                </>
              );
            }
            const trueScoreIndex = scoreIndex + ((activePage - 1) * PAGE_SIZE);

            const isScorable = !!student.activityElements[trueScoreIndex].scorable;
            // const isAutoScored = !!student.activityElements[trueScoreIndex].autoScored;
            const excludeFromScoring = !!student.activityElements[trueScoreIndex].excludeFromScoring;

            const offlineGrading = !!student.activityElements[trueScoreIndex].offlineGrading;

            const { NOT_SCORED, AI_PENDING, AI_SCORED, AI_OVERIDDEN } = GRADEBOOK_SCORE_STATE;
            const { HAS_RESPONSE_CONTENT } = GRADEBOOK_RESPONSE_CONTENT_STATE;
            const { RESPONSE_SUBMITTED } = GRADEBOOK_RESPONSE_SUBMITTED_STATE;

            const scoreState = student.scoreStates[trueScoreIndex];
            const responseContentState = student.responseContentStates[trueScoreIndex];
            const resonseSubmittedState = student.responseSubmittedStates[trueScoreIndex];
            const isResponseSubmitted = resonseSubmittedState === RESPONSE_SUBMITTED;

            let maxScore = paginatedStudentMaxScores[scoreIndex];

            if (typeof maxScore !== 'number' || !isScorable) {
              maxScore = t('notAvailableDash');
            }
            let shouldRenderPopup = isScorable;
            let scoreLabel;
            let scoreLabelText;

            const isKeyboardingType = headerInfo[trueScoreIndex].type === 'Keyboarding';
            const isSpecialScorableType = isKeyboardingType/* || ... || ... */;

            const isGradebookTypingTableAndNotKeyboardingCell = (
              isGradebookTyping && !isKeyboardingType
            );

            const isObservationalScoreCell = allowObservationalGrader && (isObservationalGraderOn || offlineGrading);

            const shouldForceDashedScore = !isObservationalScoreCell && (
              isGradebookTypingTableAndNotKeyboardingCell/* || ... || ... */
            );

            let pencilIconSize = getStyleVar('--gradebook-pencil-icon-size');
            pencilIconSize = pencilIconSize && pencilIconSize !== 'default' ? pencilIconSize : undefined;
            let iconPencilJsx;
            if (isObservationalScoreCell) {
              iconPencilJsx = (<Image className='icon-observational-pencil' src={iconObservationalPencil} />);
            } else {
              iconPencilJsx = (<Icon name='pencil' size={pencilIconSize} />);
            }
            let aiScoreState = false;
            if (scoreState === AI_SCORED || scoreState === AI_PENDING || scoreState === AI_OVERIDDEN) {
              aiScoreState = true;
            }
            // case 1: scorable and unscored
            if (isScorable && !aiScoreState && (typeof score !== 'number' || (scoreState === NOT_SCORED) || shouldForceDashedScore)) {
              if (isObservationalScoreCell) {
                scoreLabel = !isDetailScoreCellReadOnly ? iconPencilJsx : <span>—</span>;
                scoreLabelText = !isDetailScoreCellReadOnly ? t('requiresManualScore', 'Requires Manual Scoring') : '—';
              } else if (!shouldForceDashedScore && (isResponseSubmitted || isSubmitted)) {
                // eslint-disable-next-line max-len
                const shouldShowPencil = !isDetailScoreCellReadOnly && !isSpecialScorableType && responseContentState === HAS_RESPONSE_CONTENT;
                scoreLabel = shouldShowPencil ? (iconPencilJsx) : <span>—</span>;
                scoreLabelText = shouldShowPencil ? t('requiresManualScore', 'Requires Manual Scoring') : '—';
              } else {
                scoreLabel = <span>—</span>;
                scoreLabelText = '—';
              }
            // case 2: scorable, submitted and 'Scored'
            } else if (isScorable && typeof score === 'number' && !shouldForceDashedScore) {
              scoreLabel = GradebookDetailsService.getGradebookDetailsTableScorableCellLabel(score, {
                headerInfo,
                shouldRound: false,
                student,
                trueScoreIndex
              });
              scoreLabelText = scoreLabel;
            // case 3: non-scorable podium item
            } else {
              if (isObservationalScoreCell && !aiScoreState ) {
                scoreLabel = !isDetailScoreCellReadOnly ? iconPencilJsx : <span>—</span>;
                scoreLabelText = !isDetailScoreCellReadOnly ? t('requiresManualScore', 'Requires Manual Scoring') : '—';
                shouldRenderPopup = true;
              } else if (!aiScoreState) {
                scoreLabel = !shouldForceDashedScore && !isDetailScoreCellReadOnly ? (
                  <div className='view-icon-wrapper'>
                    <IconView />
                  </div>
                ) : <span>—</span>;
                scoreLabelText = !shouldForceDashedScore && !isDetailScoreCellReadOnly ? t('studentVersion', 'Student Work') : '—';
                shouldRenderPopup = true;
              }
            }

            let className = `cell-score ${trueScoreIndex}`;

            className += isScorable && excludeFromScoring ? ' excluded' : '';
            let aiIcon = null;
            let aiClassName = null;
            let aiAltText = null;
            if (isScorable && isSubmitted && (scoreState === NOT_SCORED)) {
              className += ' scorable scorable-credit';
            } else if (isScorable) {
              const creditClassName = GradebookDetailsService.getGradebookDetailsTableScoreCellCreditClassName(
                score, maxScore, isScorable, {
                  headerInfo, student, trueScoreIndex, scoreState
                }
              );
              className += ` scorable ${creditClassName}`;
            } else {
              className += ' non-scorable';
            }

            if ( scoreState === AI_PENDING) {
              aiClassName = ' aiPending';
              className += aiClassName;
              aiAltText = t('aiPending');
              aiIcon = iconAiPending;
            } else if (scoreState === AI_SCORED) {
              aiClassName = ' aiScored';
              className += aiClassName;
              aiAltText = t('aiScored');
              aiIcon = iconAiScored;
            } else if (scoreState === AI_OVERIDDEN) {
              aiClassName = ' aiOverridden';
              className += aiClassName;
              aiAltText = t('aiOverridden');
              aiIcon = iconAiOverridden;
            }

            const activityResponseId = student.activityResponseIds[trueScoreIndex];
            const elementId = student.activityElements[trueScoreIndex].entityId;
            const cellActivityElement = student.activityElements[trueScoreIndex];
            const questionStem = student.questionStems[trueScoreIndex];
            const scoreCellDetails = {
              activityId,
              activityResponseId,
              cellActivityElement,
              cellHeaderInfo: headerInfo[trueScoreIndex],
              elementId,
              excludeFromScoring,
              instanceInfo: student,
              isKeyboardingType,
              isScorable,
              maxScore,
              offlineGrading,
              questionNum: headerInfo[trueScoreIndex].number,
              questionStem,
              scoreIndex: trueScoreIndex,
              slideNumberLabel: headerInfo[trueScoreIndex].label,
              student,
              studentIndex,
              studentScore: score,
              studentScoreLabel: scoreLabel,
              tableSection: 'body',
              aiAltText,
              aiClassName,
              aiIcon
            };
            let scoreLabelCellData = <></>;

            const handleScoreCellClick = () => {
              if (isObservationalGraderOn) {
                return;
              }
              if (!isReadOnly && !isLearnosity) {
                props.onClickScoreCellDetails(scoreCellDetails);
              }
            };

            const isScoreCellDisabled = isObservationalGraderOn || isReadOnly || isLearnosity;
            const studentName = `${student.firstName} ${student.lastName}`;
            const questionNum = headerInfo[trueScoreIndex].number || headerInfo[trueScoreIndex].label || `Item ${trueScoreIndex + 1}`;

            scoreLabelCellData = (
              <div {...makeKeyboardAccessible(handleScoreCellClick, {
                ariaLabel: `${questionNum}, ${studentName}, ${t('score')}: ${scoreLabelText}`,
                disabled: isScoreCellDisabled
              })}>
                {scoreLabel}
              </div>
            );

            const scoreCellKey = `key-data-cell-${activityId}-${(student.studentId || student.id)}-${scoreIndex}`;
            const scoreTableCellClassNames = classNames(className, activeGradebookType, activeGradebookTable, {
              'is-read-only': isDetailScoreCellReadOnly,
            });

            const scoreLabelTrigger = (
              <span>{scoreLabelCellData}</span>
            );

            if (isObservationalGraderOn) {
              return (
                <GradebookObservationalScoreCell
                  key={scoreCellKey}
                  allowSort={allowSort}
                  isDetailScoreCellReadOnly={isDetailScoreCellReadOnly}
                  isLoadingGradebookDetailsTable={isLoading}
                  renderScoreLabelContent_questionStem={renderScoreLabelContent_questionStem}
                  scoreCellDetails={scoreCellDetails}
                  scoreCellKey={scoreCellKey}
                  scoreLabelTrigger={scoreLabelTrigger}
                  scoreTableCellClassNames={scoreTableCellClassNames}
                  setAllowSort={setAllowSort}
                  shouldRenderPopup={shouldRenderPopup} />
              );
            }

            let scoreLabelContent;

            if (shouldRenderPopup) {
              scoreLabelContent = {
                children: () => {
                  if (typeof props.renderDetailsBodyCellScoreLabelContent === 'function') {
                    return props.renderDetailsBodyCellScoreLabelContent(scoreCellDetails);
                  }
                  return renderDetailsBodyCellScoreLabelContent(scoreCellDetails);
                }
              };
            }
            return (
              <Table.Cell
                key={scoreCellKey}
                className={scoreTableCellClassNames}>
                {shouldRenderPopup ? renderPopup(
                  scoreLabelContent, scoreLabelTrigger,
                  'cell-score-popup', 'top center', scoreState , {
                    mouseEnterDelay: 400,
                    mouseLeaveDelay: 400,
                    // offset: [0, 16]
                  }) : scoreLabelTrigger}
                  {aiIcon !== null && (
                    <img src={aiIcon} className = {aiClassName} alt={aiAltText}/>
                  )}
              </Table.Cell>
            );
          })}
        </Table.Row>
      );
    });
    return rows;
  };

  const renderGradebookTableBodyCell = ({
    _cellId = '',
    className,
    instanceInfo,
    instanceInfoIndex,
    isReadOnly,
    key,
    label,
    tabIndex
  } = {}) => {
    const dashedCellId = kebabCase(_cellId);

    // eslint-disable-next-line prefer-template
    key = key || 'key-data-cell-' + dashedCellId + '-' + instanceInfoIndex + '-' + instanceInfo?.activityId;
    className = className || classNames(`cell-${dashedCellId}`, activeGradebookType, activeGradebookTable, {
      'is-read-only': isReadOnly
    });
    label = label || (<span>{t(_cellId)}</span>);
    tabIndex = tabIndex !== undefined ? tabIndex : 0;

    return (
      <Table.Cell
        key={key}
        className={className}
        tabIndex={tabIndex}>
        {label}
      </Table.Cell>
    );
  };

  const renderPopup = (
    content, trigger = null, className = '', position = 'top center', scoreState = null, {
      disabled = null,
      mouseEnterDelay = 0,
      mouseLeaveDelay = 0,
      offset = null,
      wide = 'very'
    } = {}
  ) => {
    const defaultClassName = 'gradebook-details-table-popup';
    className = className ? `${defaultClassName} ${className}` : defaultClassName;
    disabled = typeof disabled !== 'boolean' ? isLoading : disabled;
    if (props.waitingForGradebookSummaryTableComponent) {
      return (<span>{(trigger || typeof trigger === 'number') ? trigger : content}</span>);
    }

    return PopupService.renderPopup({
      header: scoreState === "AiScored"?t('aiScored'):null,
      className,
      // TODO remove
      // content: typeof content?.children === 'function' && loadingActivePopupContentFunction ?
      //   <Loader active inline /> : content,
      content,
      disabled,
      mouseEnterDelay,
      mouseLeaveDelay,
      offset,
      onClose: () => {
        !allowSort && setAllowSort(true);
        // TODO remove; unused // setLoadingActivePopupContentFunction(false);
      },
      onOpen: () => {
        // placeholder
      },
      position,
      trigger,
      wide
    });
  };

  /**
   * @param {{content: string | any, translationKey: string}[]} contents
   */
  const renderContentsWithLabel = (contents = []) => {
    return (
      <div className={classNames('cell-score-popup-content-wrapper', activeGradebookType, activeGradebookTable)}>
        {contents.map((current, index) => {
          const { content, translationKey } = current;
          return (
            <div key={`content-${current.translationKey}-${index}`}
              className={`content ${current.translationKey} ${index}`}>
              <span>
                {t(translationKey)}
                {' '}
                {content === '—' ? t('notAvailable') : content}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDetailsHeaderCellScoreLabelContent = (scoreCellDetails) => {
    return renderDetailsBodyCellScoreLabelContent(scoreCellDetails);
  };

  const renderDetailsBodyCellScoreLabelContent = (scoreCellDetails) => {
    return (
      <div className={classNames('cell-score-popup-content-wrapper', activeGradebookType, activeGradebookTable)}>
        {renderScoreLabelContent_questionStem(scoreCellDetails)}
        {renderScoreLabelContent_maxScore(scoreCellDetails)}
        {!isLearnosity && renderScoreLabelContent_viewedTime(scoreCellDetails)}
        {renderScoreLabelContent_questionNumberLabel(scoreCellDetails)}
        {(scoreCellDetails.slideNumberLabel) && renderScoreLabelContent_slideNumberLabel(scoreCellDetails)}
        {!isReadOnly && !isLearnosity &&
          renderScoreLabelContent_infoForExcludeFromScore(scoreCellDetails)}
      </div>
    );
  };

  // TODO remove; unused
  // const renderScoreLabelContent_surveyQuestionLabel = (scoreCellDetails) => {
  //   const { isSurvey, tableSection } = scoreCellDetails;
  //   const isFromHeader = tableSection === 'header';
  //   return isFromHeader && isSurvey ? (
  //     <>
  //       <div className='cell-survey-question-label'>
  //         {t('surveyQuestionLabel')}
  //       </div>
  //     </>
  //   ) : null;
  // };

  const renderScoreLabelContent_questionStem = (scoreCellDetails) => {
    let { questionStem } = scoreCellDetails;
    questionStem = stripImgHtmlTags(questionStem);
    
    return questionStem && (
      <>
        <div className='cell-question-stem'>
          {UtilityService.reactHtmlParserWrapper(questionStem).parsed}
        </div>
        <div className='cell-question-stem-bottom-spacer'>
          {/* placeholder */}
        </div>
      </>
    );
  };

  const renderScoreLabelContent_maxScore = (scoreCellDetails) => {
    let { maxScore } = scoreCellDetails;
    maxScore = maxScore === t('notAvailableDash') ? t('notAvailable') : maxScore;
    return maxScore && (
      <div className='cell-max-score-label'>
        {t('maxScoreLabel', { maxScore })}
      </div>
    );
  };

  const renderScoreLabelContent_viewedTime = (scoreCellDetails) => {
    const { scoreIndex, student } = scoreCellDetails;
    if (!student || !student.usages) {
      return <div className='cell-viewed-time-placeholder' />;
    }

    const viewedTimeInSeconds = student.usages[scoreIndex] || 0;

    const viewedTimeTranslationKey = scoreCellDetails.translationKey || 'viewedTime';

    const usageLabel = UsageService.getUnroundedUsageLabel(viewedTimeInSeconds);
    return (
      <div className='cell-viewed-time-label'>
        {t(viewedTimeTranslationKey)}{' '}{usageLabel}
      </div>
    );
  };

  const renderScoreLabelContent_slideNumberLabel = (scoreCellDetails) => {
    const slideNumberLabel = scoreCellDetails.slideNumberLabel.replace('Slide', t('slideLabel'));
    return slideNumberLabel && (
      <>
        <div className='cell-slide-number-label'>
          {slideNumberLabel}
        </div>
      </>
    );
  };

  const renderScoreLabelContent_questionNumberLabel = (scoreCellDetails) => {
    if (scoreCellDetails.questionNum !== null && scoreCellDetails.questionNum !== 'null') {
      const questionNumberLabel = `${t('questionLabel', 'Question')}: ${scoreCellDetails.questionNum}`;
      return questionNumberLabel && (
        <>
          <div className='cell-slide-number-label'>
            {questionNumberLabel}
          </div>
        </>
      );
    }
  };

  const renderScoreLabelContent_infoForExcludeFromScore = (scoreCellDetails) => {
    const { cellActivityElement, isScorable, tableSection } = scoreCellDetails;
    let { activityId } = scoreCellDetails;
    const isFromBody = tableSection === 'body';
    const isFromHeader = tableSection === 'header';

    if (!isScorable) {
      return null;
    }
    activityId = activityId || gradebookManager.currentAssignmentId;
    if (!activityId) {
      throw new TypeError('renderScoreLabelContent_infoForExcludeFromScore:' +
        '`scoreCellDetails.activityId` || `gradebookManager.currentAssignmentId` is required');
    }
    const assignment = assignmentManager.assignments.get(activityId);
    if (!assignment) {
      // `assignment` is likely still being fetched from `assignmentManager.fetchActivity` via handleLoadMore().
      // renderScoreLabelContent_infoForExcludeFromScore() will rerender once handleLoadMore() is finished
      return null;
    }
    if (!cellActivityElement) {
      throw new TypeError('renderScoreLabelContent_infoForExcludeFromScore: `scoreCellDetails.cellActivityElement` is required');
    }
    let excludeFromScoring;
    if (isFromHeader) {
      excludeFromScoring = scoreCellDetails.excludeFromScoring;
    } else {
      excludeFromScoring = cellActivityElement.excludeFromScoring;
    }

    const graded = assignment.status === ASSIGNMENT_STATUS.COMPLETED;
    const hasAnyGraded = gradebookManager.gradebookDetails.hasGraded; // for header

    const isAssignedToClassroom = assignment.assignEntityTypeId === ASSIGNMENT_TYPE.CLASSROOM;
    if (activeGradebookType !== 'aggregate' && isFromBody && isAssignedToClassroom) {
      // if 'nonAggregate' and assigned to entire classroom, only allow 'excludeFromScoring' in the table header cells
      return null;
    }
    return (
      <>
        <div className='cell-info-for-exclude-from-score-container'>
          <div className='exclude-from-score-checkbox-wrapper'>
            <Checkbox
              checked={excludeFromScoring}
              disabled={(isFromBody && graded) || (isFromHeader && hasAnyGraded)}
              label={t('excludeFromScore_checkboxLabel')}
              onClick={(event, data) => props.handleExcludeItemFromScore(event, { ...data, scoreCellDetails })}
              onMouseEnter={() => allowSort && setAllowSort(false)} />
          </div>
          {(isFromHeader && hasAnyGraded) && (
            <div className='exclude-from-score-description-wrapper'>
              {t('excludeFromScore_checkboxGradedWarning')}
            </div>
          )}
          {(isFromBody && graded) && (
            <>
              <div className='exclude-from-score-description-wrapper'>
                {t('excludeFromScore_descriptionForAggregate')}
              </div>
              <div className='exclude-from-score-button-wrapper'>
                <Button
                  loading={props.waitingForGradebookSummaryTableComponent}
                  onClick={(event, data) => props.handleBringStatusBackToClosed(event, { ...data, assignment, scoreCellDetails })}
                  onMouseEnter={() => allowSort && setAllowSort(false)}
                  primary>
                  {t('excludeFromScore_buttonLabel')}
                </Button>
              </div>
            </>
          )}
        </div>
      </>
    );
  };

  const totalPages = gradebookManager.gradebookDetailsTableHorizontalScoresPageTotal;

  const renderUnscorableColumnsToggler = () => {
    const hideUnscorable = gradebookManager.shouldHideUnscorableColumns;

    const shouldHideUnscorableColumns = isObservationalGraderOn ? true : (
      gradebookManager.shouldHideUnscorableColumns
    );
    const unscorableTogglerDisabledRollover = isObservationalGraderOn ? (
      t('unscorableTogglerDisabledRollover')
    ) : undefined;

    const hideUnscorableLabel = t(hideUnscorable ? 'hideUnscorableTrueLabel' : 'hideUnscorableFalseLabel');

    const handleKeyboardToggle = () => {
      if (!isObservationalGraderOn) {
        const syntheticEvent = {};
        const syntheticData = { checked: !hideUnscorable };
        handleToggleHideUnscorableColumns(syntheticEvent, syntheticData);
      }
    };

    return PopupService.renderPopup({
      content: unscorableTogglerDisabledRollover,
      disabled: !unscorableTogglerDisabledRollover,
      position: 'top left',
      trigger: (
        <div
          className='unscorable-columns-toggler toggle-hide-or-show-wrapper'
          {...makeKeyboardAccessible(
            handleKeyboardToggle,
            {
              ariaLabel: hideUnscorableLabel,
              disabled: isObservationalGraderOn,
              role: 'switch',
              stopPropagationOnChildren: true
            }
          )}
          aria-checked={hideUnscorable}>
          <Checkbox
            className='hide-or-show-toggler hide-or-show-toggler-directly-on-background'
            data-content={t(hideUnscorable ? 'togglerLabelShow' : 'togglerLabelHide')}
            checked={shouldHideUnscorableColumns}
            disabled={isObservationalGraderOn}
            label={hideUnscorableLabel}
            onChange={handleToggleHideUnscorableColumns}
            toggle />
        </div>
      ),
      wide: false
    });
  };

  const renderObservationalGraderToggler = () => {
    const { hasObservationalGradebookData, observationalGraderToggleStatus } = gradebookManager;

    const toggledOn = observationalGraderToggleStatus === OBSERVATIONAL_GRADER_TOGGLE_STATUS.ACTIVE;
    const observationalGraderLabel = t(toggledOn ? 'observationalGraderActiveLabel' : 'observationalGraderInactiveLabel');

    const handleKeyboardToggle = () => {
      const syntheticEvent = {};
      const syntheticData = { checked: !toggledOn };
      handleToggleObservationalGrader(syntheticEvent, syntheticData);
    };

    return hasObservationalGradebookData ? (
      <div
        className='observational-grader-toggler toggle-active-or-inactive-wrapper toggle-hide-or-show-wrapper'
        {...makeKeyboardAccessible(
          handleKeyboardToggle,
          {
            ariaLabel: observationalGraderLabel,
            role: 'switch',
            stopPropagationOnChildren: true
          }
        )}
        aria-checked={toggledOn}>
        <Checkbox
          className='hide-or-show-toggler hide-or-show-toggler-directly-on-background'
          data-content={t(toggledOn ? 'togglerLabelShow' : 'togglerLabelHide')}
          defaultChecked={toggledOn}
          label={observationalGraderLabel}
          onChange={handleToggleObservationalGrader}
          toggle />
        <InfoIcon className='ObservationalGraderTogglerInfoIcon custom-styles font-size-theme'
          popupOptions={{ content: t('observationalGraderTogglerRollover'), on: 'click' }} />
      </div>
    ) : null;
  };

  return (
    <div className={classNames('gradebook-details-table-container', activeGradebookType, activeGradebookTable)}>
      {gradebookManager.gradebookDetails ? (
        <>
          <div className='gradebook-details-top'>
            {/* TOP LEFT (togglers) */}
            <div className='gradebook-details-topleft'>
              {!isLearnosity && renderUnscorableColumnsToggler()}
              {allowObservationalGrader && isGradebookDetails && renderObservationalGraderToggler()}
            </div>
            {/* TOP CENTER (placeholder) */}
            <div className='gradebook-details-topcenter'>
              {/* placeholder */}
            </div>
            {/* TOP RIGHT (paginator) */}
            <div className='gradebook-details-topright'>
              <div className='gradebook-details-paginator-outer-wrapper'>
                <Pagination
                  activePage={gradebookManager.activeGradebookDetailsHorizontalScoresPage}
                  onPageChange={handleHorizontalScoresPageChange}
                  totalPages={totalPages} />
              </div>
            </div>
          </div>

          <div key={scrollerKey}>
            <InfiniteScroll
              hasMore={!isLoading && allowLoadMoreAggregateTableRows && hasMoreAggregateTableRows}
              initialLoad={true}
              loadMore={(page) => allowLoadMoreAggregateTableRows && handleLoadMore(page)}>
              <Table
                className={`gradebook-details-table${isLoading ? ' loading-table' : ''}`}
                sortable
                striped>
                <Table.Header>
                  {renderTableHeader()}
                </Table.Header>
                <Table.Body>
                  {renderTableBodyRows()}
                </Table.Body>
              </Table>
              <Loader
                active={isLoading && allowLoadMoreAggregateTableRows && hasMoreAggregateTableRows}
                inline='centered'
                style={{ marginBottom: '14px' }} />
            </InfiniteScroll>
          </div>
        </>
      ) : <Loader active />}
    </div>
  );
});
export default GradebookDetailsTable;

SatCoreRegister('GradebookDetailsTable', GradebookDetailsTable);
